import React from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import DragNumberInput from "../ui/DragNumberInput";
import FieldLabel from "../ui/FieldLabel";
import InfoTooltip from "../ui/InfoTooltip";
import MenuSelect from "../ui/MenuSelect";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";

const LIMIT_OPTIONS = [
  {
    value: "false",
    label: "Off (range is stored, not enforced)",
  },
  { value: "true", label: "On (physical stop at min/max)" },
  { value: "auto", label: "Auto (use imported/default setting)" },
];

const DYNAMICS_FIELDS = [
  {
    key: "stiffness",
    label: "Stiffness",
    property: "stiffness",
    tooltipContent: "Higher values resist joint motion",
  },
  {
    key: "damping",
    label: "Damping",
    property: "damping",
    tooltipContent: "Higher values resist velocity",
  },
  {
    key: "frictionloss",
    label: "Friction",
    property: "frictionloss",
    tooltipContent: "Static joint friction term",
  },
];

function JointPhysicsSection({
  disabled,
  editingInputs,
  hasScalarValueRange,
  joint,
  onDampingChange,
  onFrictionLossChange,
  onStiffnessChange,
  onUseLimitsChange,
  setEditingInputs,
}) {
  const changeHandlers = {
    damping: onDampingChange,
    frictionloss: onFrictionLossChange,
    stiffness: onStiffnessChange,
  };

  return (
    <InspectorPhysicsLockedCollapsibleWrap locked={disabled}>
      <Collapsible defaultOpen>
        <CollapsibleTrigger quiet>Physics</CollapsibleTrigger>
        <CollapsibleContent>
          <fieldset
            disabled={disabled}
            className={
              disabled
                ? "pt-2 space-y-4 text-sm text-gray-300 opacity-60 pointer-events-none"
                : "pt-2 space-y-4 text-sm text-gray-300"
            }
          >
            {hasScalarValueRange && (
              <div>
                <FieldLabel className="inline-flex items-center gap-1.5 mb-1">
                  Enforce movement limits
                  <InfoTooltip
                    text={
                      "Controls whether the simulator treats this joint's min/max range as a physical stop.\n" +
                      "Turn it off when the range is only metadata, such as a repeating or wrapping joint."
                    }
                    widthClass="w-72"
                  />
                </FieldLabel>
                <MenuSelect
                  value={joint.useLimits ?? "auto"}
                  onChange={onUseLimitsChange}
                  disabled={disabled}
                  options={LIMIT_OPTIONS}
                  aria-label="Enforce movement limits"
                />
              </div>
            )}

            <div>
              <FieldLabel className="block mb-2">Dynamics</FieldLabel>
              <div className="grid grid-cols-3 gap-2">
                {DYNAMICS_FIELDS.map(({ key, label, property, tooltipContent }) => {
                  const baseValue = Number(joint?.[property] ?? 0);
                  const displayValue =
                    editingInputs[key] !== undefined ? editingInputs[key] : baseValue;
                  const handleChange = changeHandlers[key];
                  return (
                    <div key={key} className="space-y-1">
                      <DragNumberInput
                        label={label}
                        value={displayValue}
                        dragValue={baseValue}
                        onDragValueChange={(v) => handleChange(v)}
                        onChange={(e) => {
                          setEditingInputs((prev) => ({ ...prev, [key]: e.target.value }));
                          handleChange(e.target.value);
                        }}
                        onFocus={(e) => {
                          setEditingInputs((prev) => ({ ...prev, [key]: e.target.value }));
                        }}
                        onBlur={(e) => {
                          handleChange(e.target.value);
                          setEditingInputs((prev) => {
                            const next = { ...prev };
                            delete next[key];
                            return next;
                          });
                        }}
                        tooltipContent={tooltipContent}
                        wrapperClassName="space-y-1"
                        labelClassName="inline-flex items-center gap-1 text-xs panel-text-helper"
                        inputClassName="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm"
                        inputProps={{ step: "0.1", min: "0" }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </fieldset>
        </CollapsibleContent>
      </Collapsible>
    </InspectorPhysicsLockedCollapsibleWrap>
  );
}

export default React.memo(JointPhysicsSection);
